// CiAnimations: FLIP-style row motion for pipeline-list filter changes.

import gsap from "gsap";
import type { LiveViewHook, CiPushEventWire } from "../types";

type CiHookLike = LiveViewHook<CiPushEventWire>;

type RowRect = {
  top: number;
  left: number;
};

type CiAnimationsState = CiHookLike & {
  _rowSnapshot?: Map<string, RowRect>;
  _observer?: MutationObserver;
  _pendingFlip?: boolean;
  _raf?: number;
  _armChangeHandler?: EventListener;
  _armClickHandler?: EventListener;
};

const ROW_SELECTOR = ".pipeline-row";
const FILTER_BAR_SELECTOR = "#pipeline-filter-bar";

const rowSelector = (hook: CiHookLike): string =>
  hook.el.dataset["rowSelector"] ?? ROW_SELECTOR;

const armRoot = (hook: CiHookLike): HTMLElement | null => {
  const selector = hook.el.dataset["armRootSelector"];
  if (selector) return hook.el.closest<HTMLElement>(selector);

  const dashboard = hook.el.closest("pipeline-dashboard");
  const inDashboard =
    dashboard?.querySelector<HTMLElement>(FILTER_BAR_SELECTOR) ?? null;
  if (inDashboard) return inDashboard;

  // The pipelines page lifted its filter selects out of <pipeline-dashboard>
  // and into the tab-bar row above it, so they're siblings rather than
  // descendants now. Fall back to a document-wide lookup so the arm
  // wiring still finds them.
  return document.querySelector<HTMLElement>(FILTER_BAR_SELECTOR);
};

const armChangeSelector = (hook: CiHookLike): string =>
  hook.el.dataset["armChangeSelector"] ?? "select";

const armClickSelector = (hook: CiHookLike): string =>
  hook.el.dataset["armClickSelector"] ?? "a[href]";

const snapshotRows = (
  root: HTMLElement,
  selector: string,
): Map<string, RowRect> => {
  const rows = root.querySelectorAll<HTMLElement>(selector);
  const snapshot = new Map<string, RowRect>();

  rows.forEach((row) => {
    if (!row.id) return;
    const rect = row.getBoundingClientRect();
    snapshot.set(row.id, { top: rect.top, left: rect.left });
  });

  return snapshot;
};

const runFlip = (hook: CiAnimationsState): void => {
  const prev = hook._rowSnapshot;
  hook._pendingFlip = false;
  delete hook._rowSnapshot;

  if (!prev || prev.size === 0) return;

  const rows = hook.el.querySelectorAll<HTMLElement>(rowSelector(hook));

  let moveIndex = 0;
  let enterIndex = 0;

  rows.forEach((row) => {
    if (!row.id) return;

    gsap.killTweensOf(row);

    const next = row.getBoundingClientRect();
    const prior = prev.get(row.id);

    if (prior) {
      const dx = prior.left - next.left;
      const dy = prior.top - next.top;

      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
        gsap.fromTo(
          row,
          { x: dx, y: dy },
          {
            x: 0,
            y: 0,
            duration: 0.35,
            delay: moveIndex * 0.015,
            ease: "power2.out",
            clearProps: "transform",
          },
        );
        moveIndex += 1;
      }

      return;
    }

    gsap.fromTo(
      row,
      { y: 12, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.25,
        delay: enterIndex * 0.02,
        ease: "power2.out",
        clearProps: "transform,opacity",
      },
    );
    enterIndex += 1;
  });
};

const scheduleFlip = (hook: CiAnimationsState): void => {
  if (!hook._pendingFlip || hook._raf) return;

  hook._raf = window.requestAnimationFrame(() => {
    hook._raf = 0;
    runFlip(hook);
  });
};

const armFlip = (hook: CiAnimationsState): void => {
  const snapshot = snapshotRows(hook.el, rowSelector(hook));
  if (snapshot.size === 0) return;
  hook._rowSnapshot = snapshot;
  hook._pendingFlip = true;
};

export const CiAnimations = {
  mounted(this: CiAnimationsState) {
    const root = armRoot(this);
    const changeSelector = armChangeSelector(this);
    const clickSelector = armClickSelector(this);

    const handleArmChange: EventListener = (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest(changeSelector)) return;
      armFlip(this);
    };

    const handleArmClick: EventListener = (event) => {
      const target =
        event.target instanceof Element
          ? event.target.closest(clickSelector)
          : null;
      if (!target) return;
      armFlip(this);
    };

    if (root) {
      root.addEventListener("change", handleArmChange, true);
      root.addEventListener("click", handleArmClick, true);
      this._armChangeHandler = handleArmChange;
      this._armClickHandler = handleArmClick;
    }

    const rowsSelector = rowSelector(this);
    const observer = new MutationObserver((mutations) => {
      if (!this._pendingFlip) return;

      const touchedRows = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) =>
            node instanceof HTMLElement &&
            (node.matches(rowsSelector) || !!node.querySelector(rowsSelector)),
        ),
      );

      if (touchedRows) scheduleFlip(this);
    });

    observer.observe(this.el, { childList: true, subtree: true });
    this._observer = observer;
  },

  destroyed(this: CiAnimationsState) {
    if (this._raf) window.cancelAnimationFrame(this._raf);
    this._observer?.disconnect();

    const root = armRoot(this);

    if (root && this._armChangeHandler) {
      root.removeEventListener("change", this._armChangeHandler, true);
    }

    if (root && this._armClickHandler) {
      root.removeEventListener("click", this._armClickHandler, true);
    }
  },
};
